import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { LoadingState } from '../../utilities/constants';
import { toast } from 'react-toastify';
import {
  uploadBillToS3Api,
  fetchBillsListApi,
  fetchBillEmissionsApi,
  downloadBillApi,
  parseBatchApi,
  saveBatchApi
} from './billParsingApi';

export const fetchBillsList = createAsyncThunk('billParsing/fetchList', async (payload) => {
  try {
    const { orgId, page, limit } = payload;
    const response = await fetchBillsListApi(orgId, page, limit);
    return response;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

export const fetchBillEmissions = createAsyncThunk('billParsing/fetchBillEmissions', async (payload) => {
  try {
    const { orgId, upldflId } = payload;
    const response = await fetchBillEmissionsApi(orgId, upldflId);
    return response;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

export const downloadBill = createAsyncThunk('billParsing/download', async (payload) => {
  try {
    const { orgId, fileId } = payload;
    const response = await downloadBillApi(orgId, fileId);
    window.open(response.downloadUrl, '_blank');
    return response;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

export const uploadAndParseBatch = createAsyncThunk('billParsing/uploadAndParseBatch', async (payload) => {
  try {
    const { orgId, files } = payload;
    const uploadedFiles = [];
    for (const file of files) {
      const { key, name } = await uploadBillToS3Api(orgId, file);
      uploadedFiles.push({ key, name });
    }
    const parseResponse = await parseBatchApi(orgId, uploadedFiles);
    return parseResponse;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

export const saveBillBatch = createAsyncThunk('billParsing/saveBatch', async (payload) => {
  try {
    const { orgId, bills } = payload;
    const result = await saveBatchApi(orgId, bills);
    if (result?.summary) {
      const { successfulBills, failedBills, totalEmissions } = result.summary;

      if (failedBills === 0) {
        toast.success(`Successfully saved ${successfulBills} bills with ${totalEmissions} emissions`);
      }
    }
    return result;
  } catch (error) {
    toast.error(error.message);
    throw error;
  }
});

const billParsingSlice = createSlice({
  name: 'billParsing',
  initialState: {
    listStatus: LoadingState.idle,
    bills: [],
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    batchParseResults: null,
    batchParseStatus: LoadingState.idle,
    batchSaveStatus: LoadingState.idle,
    batchSaveResult: null,
    billEmissions: { entity: null, emissions: [] },
    billEmissionsStatus: LoadingState.idle
  },
  reducers: {
    clearParsedData: (state) => {
      state.batchParseResults = null;
      state.batchParseStatus = LoadingState.idle;
      state.batchSaveStatus = LoadingState.idle;
      state.batchSaveResult = null;
    },
    clearBillEmissions: (state) => {
      state.billEmissions = { entity: null, emissions: [] };
      state.billEmissionsStatus = LoadingState.idle;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillsList.pending, (state) => {
        state.listStatus = LoadingState.pending;
      })
      .addCase(fetchBillsList.fulfilled, (state, action) => {
        state.listStatus = LoadingState.succeeded;
        state.bills = Array.isArray(action.payload) ? action.payload : [];
        state.total = state.bills.length;
        state.totalPages = Math.ceil(state.total / state.limit);
      })
      .addCase(fetchBillsList.rejected, (state) => {
        state.listStatus = LoadingState.failed;
      })
      .addCase(fetchBillEmissions.pending, (state) => {
        state.billEmissionsStatus = LoadingState.pending;
      })
      .addCase(fetchBillEmissions.fulfilled, (state, action) => {
        state.billEmissionsStatus = LoadingState.succeeded;
        state.billEmissions = {
          entity: action.payload?.entity ?? null,
          emissions: Array.isArray(action.payload?.emissions) ? action.payload.emissions : []
        };
      })
      .addCase(fetchBillEmissions.rejected, (state) => {
        state.billEmissionsStatus = LoadingState.failed;
      })
      .addCase(uploadAndParseBatch.pending, (state) => {
        state.batchParseStatus = LoadingState.pending;
      })
      .addCase(uploadAndParseBatch.fulfilled, (state, action) => {
        state.batchParseStatus = LoadingState.succeeded;
        state.batchParseResults = action.payload;
      })
      .addCase(uploadAndParseBatch.rejected, (state) => {
        state.batchParseStatus = LoadingState.failed;
      })
      .addCase(saveBillBatch.pending, (state) => {
        state.batchSaveStatus = LoadingState.pending;
        state.batchSaveResult = null;
      })
      .addCase(saveBillBatch.fulfilled, (state, action) => {
        state.batchSaveStatus = LoadingState.succeeded;
        state.batchSaveResult = action.payload;
      })
      .addCase(saveBillBatch.rejected, (state) => {
        state.batchSaveStatus = LoadingState.failed;
      });
  }
});

export const { clearParsedData, clearBillEmissions } = billParsingSlice.actions;
export default billParsingSlice.reducer;
